import React, { useMemo } from 'react';

function getHostname(source) {
  if (source?.domain) return source.domain.replace(/^www\./, '');

  if (source?.url) {
    try {
      return new URL(source.url).hostname.replace(/^www\./, '');
    } catch {
      return '';
    }
  }

  return '';
}

export default function SourcesList({ resources = [], isDarkMode }) {
  const allSources = useMemo(() => {
    if (!Array.isArray(resources)) return [];

    const sources = resources
      .filter((res) => res && Array.isArray(res.sources))
      .flatMap((res) => res.sources)
      .filter((src) => src && typeof src === 'object');

    const seen = new Set();

    return sources.filter((src) => {
      const key = src.url || `${src.domain || ''}-${src.title || ''}`;

      if (!key || seen.has(key)) return false;

      seen.add(key);
      return true;
    });
  }, [resources]);

  if (!allSources.length) return null;

  return (
    <section className="mt-3 px-1" aria-label="Sources">
      <div
        className={`rounded-xl border p-3 ${
          isDarkMode
            ? 'border-gray-700 bg-[#151515]'
            : 'border-[#E5E5E5] bg-[#FAFAFA]'
        }`}
      >
        <div
          className={`mb-3 flex items-center justify-between border-b pb-2 ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <span
            className={`text-[13px] font-medium ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}
          >
            Sources
          </span>

          <span
            className={`text-[12px] ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`}
          >
            {allSources.length}
          </span>
        </div>

        <div
          className={`overflow-x-auto pb-2 pr-1 sources-scrollbar ${
            isDarkMode ? 'sources-scrollbar-dark' : 'sources-scrollbar-light'
          }`}
        >
          <div className="flex min-w-max gap-3">
            {allSources.map((src, idx) => {
              const hostname = getHostname(src);
              const title = src?.title || 'Untitled Source';
              const href = src?.url || null;

              const favicon = hostname
                ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(
                    hostname,
                  )}&sz=32`
                : null;

              return (
                <a
                  key={href || `${hostname}-${title}-${idx}`}
                  href={href || undefined}
                  target={href ? '_blank' : undefined}
                  rel={href ? 'noopener noreferrer' : undefined}
                  aria-disabled={!href}
                  title={title}
                  className={`w-[220px] shrink-0 rounded-lg border px-3 py-2 transition-all duration-200 ${
                    isDarkMode
                      ? 'border-gray-700 bg-[#101010] hover:border-gray-500 hover:bg-[#141414]'
                      : 'border-[#D9D9D9] bg-white hover:border-[#C9C9C9] hover:shadow-sm'
                  } ${!href ? 'pointer-events-none opacity-70' : ''}`}
                >
                  <div className="mb-1.5 flex items-center gap-2">
                    {favicon ? (
                      <img
                        src={favicon}
                        alt=""
                        loading="lazy"
                        className="h-4 w-4 shrink-0 rounded-sm object-contain"
                      />
                    ) : (
                      <div
                        className={`h-4 w-4 shrink-0 rounded-sm ${
                          isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                        }`}
                      />
                    )}

                    <span
                      title={hostname || 'Unknown source'}
                      className={`truncate text-[12px] leading-none ${
                        isDarkMode ? 'text-gray-400' : 'text-[#777]'
                      }`}
                    >
                      {hostname || 'Unknown source'}
                    </span>
                  </div>

                  <div
                    className={`line-clamp-2 text-[13px] font-medium leading-5 ${
                      isDarkMode ? 'text-gray-100' : 'text-[#222]'
                    }`}
                  >
                    {title}
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
